import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n/languages";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  const segments = location.pathname.split("/").filter(Boolean);
  const lang = normalizeLang(segments[0]);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatsOpen(false);
  }, [location.pathname]);

  const textColor = scrolled ? "text-foreground-900" : "text-white";
  const borderColor = scrolled ? "border-background-200/70" : "border-white/15";
  const bgClass = scrolled ? "bg-background-50/95 backdrop-blur-md" : "bg-transparent";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${bgClass} border-b ${borderColor}`}
    >
      <nav className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3.5 md:px-6">
        <Link to={`/${lang}`} className="flex items-center gap-2 cursor-pointer">
          <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${scrolled ? "bg-primary-500 text-white" : "bg-white/15 text-white"} transition-colors`}>
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className={`font-body text-base font-bold ${textColor} whitespace-nowrap transition-colors`}>
            AllChinaBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <Link
            to={`/${lang}`}
            className={`text-sm font-semibold ${textColor} hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer`}
          >
            {t("nav.home")}
          </Link>
          <a
            href="#categories"
            className={`text-sm font-semibold ${textColor} hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer`}
          >
            {t("nav.categories")}
          </a>
          <a
            href="#guides"
            className={`text-sm font-semibold ${textColor} hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer`}
          >
            {t("nav.articles")}
          </a>
          <a
            href="#faq"
            className={`text-sm font-semibold ${textColor} hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer`}
          >
            FAQ
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
              scrolled
                ? "bg-foreground-950 text-white hover:bg-foreground-800"
                : "bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm"
            }`}
          >
            AllChinaBuy Sheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled
                ? "text-foreground-900 border border-background-200/70"
                : "text-white border border-white/20"
            }`}
            aria-label="Menu"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 py-4 max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to={`/${lang}`}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-home-4-line"></i>
              </span>
              {t("nav.home")}
            </Link>
            <a
              href="#categories"
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-layout-grid-line"></i>
              </span>
              {t("nav.categories")}
            </a>
            <a
              href="#guides"
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-book-open-line"></i>
              </span>
              {t("nav.articles")}
            </a>
            <a
              href="#faq"
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-questionnaire-line"></i>
              </span>
              FAQ
            </a>

            <button
              type="button"
              onClick={() => setCatsOpen((v) => !v)}
              className="mt-1 flex items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                  <i className="ri-price-tag-3-line"></i>
                </span>
                {t("dropdown.browse")}
              </span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`${
                    catsOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                  } text-foreground-500`}
                ></i>
              </span>
            </button>

            {catsOpen && (
              <div className="mt-1 grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600 shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="truncate">{cat.name[lang]}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}